import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShoppingCart, Plus, Trash2, Send, CheckCircle2, PackageCheck, XCircle,
  ArrowLeft, BarChart3, TrendingUp, Receipt, Building2, Radar,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Button, Modal, Select } from "../lib/ui";
import { JournalPreview } from "../components/JournalPreview";
import { useToast } from "../lib/toast";
import { fmtRp, fmtNum, fmtUnit } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";

const PO_STATUS = {
  DRAFT: "mute", SUBMITTED: "warn", APPROVED: "info", RECEIVED: "success", CANCELLED: "danger",
};
const ACTION_MSG = { submit: "PO submitted", approve: "PO approved", cancel: "PO cancelled" };

export default function Procurement() {
  const toast = useToast();
  const { isRole } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [pos, setPos] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState(null);
  const [createModal, setCreateModal] = useState(false);
  const [form, setForm] = useState({ supplier_id: "", notes: "", items: [] });
  const [receiveModal, setReceiveModal] = useState(false);
  const [receiveItems, setReceiveItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const canCreate = isRole("SUPER_ADMIN", "CENTRAL_PURCHASING", "WAREHOUSE");
  const canApprove = isRole("SUPER_ADMIN", "CENTRAL_PURCHASING");
  const canReceive = isRole("SUPER_ADMIN", "CENTRAL_PURCHASING", "WAREHOUSE");

  const load = useCallback(() => {
    api.get("/purchase-orders").then(({ data }) => setPos(data));
    api.get("/analytics/purchasing").then(({ data }) => setAnalytics(data));
  }, []);
  useEffect(() => {
    load();
    api.get("/suppliers").then(({ data }) => setSuppliers(data));
    api.get("/products?active_only=true&visible_in=central_procurement").then(({ data }) => setProducts(data));
  }, [load]);

  const openDetail = async (id) => { const { data } = await api.get(`/purchase-orders/${id}`); setActive(data); };

  const addLine = () => setForm((f) => ({ ...f, items: [...f.items, { product_id: "", quantity: "", unit_price: "" }] }));
  const addCustomLine = () => setForm((f) => ({ ...f, items: [...f.items, { is_custom: true, custom_name: "", quantity: "", unit: "", unit_price: "", supplier_id: "", notes: "" }] }));
  const setLine = (i, patch) => setForm((f) => ({ ...f, items: f.items.map((x, idx) => idx === i ? { ...x, ...patch } : x) }));
  const removeLine = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const onPickProduct = (i, product_id) => {
    const p = products.find((x) => x.id === product_id);
    let price = p?.price || 0;
    const pref = (p?.suppliers || []).find((s) => s.preferred) || (p?.suppliers || [])[0];
    if (pref) price = pref.price;
    setLine(i, { product_id, unit_price: price });
  };

  const createPO = async () => {
    const items = form.items
      .filter((it) => (it.is_custom ? (it.custom_name && Number(it.quantity) > 0) : (it.product_id && Number(it.quantity) > 0)))
      .map((it) => it.is_custom
        ? { is_custom: true, custom_name: it.custom_name, quantity: Number(it.quantity), unit: it.unit || null, unit_price: it.unit_price === "" ? 0 : Number(it.unit_price), supplier_id: it.supplier_id || null, notes: it.notes || null }
        : { product_id: it.product_id, quantity: Number(it.quantity), unit_price: it.unit_price === "" ? null : Number(it.unit_price) });
    if (items.length === 0) return toast.error(t("Add at least one item"));
    try {
      await api.post("/purchase-orders", { supplier_id: form.supplier_id || null, items, notes: form.notes, source: "manual" });
      toast.success(t("Draft purchase order created"));
      setCreateModal(false); setForm({ supplier_id: "", notes: "", items: [] }); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };

  const action = async (id, act) => {
    if (act === "cancel" && !window.confirm(t("Cancel this purchase order?"))) return;
    setBusy(true);
    try {
      await api.post(`/purchase-orders/${id}/${act}`);
      toast.success(t(ACTION_MSG[act] || "Updated"));
      const { data } = await api.get(`/purchase-orders/${id}`); setActive(data); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setBusy(false); }
  };

  const openReceive = () => {
    setReceiveItems(active.items.filter((it) => !it.is_custom && it.product_id).map((it) => ({ ...it, received_quantity: it.quantity, batch_number: "", expiry_date: "" })));
    setReceiveModal(true);
  };
  const submitReceive = async () => {
    setBusy(true);
    try {
      const items = receiveItems.map((it) => ({
        product_id: it.product_id, received_quantity: Number(it.received_quantity),
        unit_price: it.unit_price === "" ? null : Number(it.unit_price),
        batch_number: it.batch_number || null, expiry_date: it.expiry_date || null,
      }));
      await api.post(`/purchase-orders/${active.id}/receive`, { items });
      toast.success(t("Goods received & stocked in"));
      setReceiveModal(false);
      const { data } = await api.get(`/purchase-orders/${active.id}`); setActive(data); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setBusy(false); }
  };

  if (!pos) return <PageLoader />;

  // ---- PO detail ----
  if (active) {
    return (
      <div className="animate-fade-up">
        <PageHeader title={`PO ${active.po_no}`} subtitle={`${active.supplier_name} · ${t(active.status)}`} icon={ShoppingCart}>
          <Button variant="outline" size="sm" onClick={() => setActive(null)} data-testid="po-back"><ArrowLeft size={16} /> {t("Back")}</Button>
          {active.status === "DRAFT" && canCreate && <Button size="sm" onClick={() => action(active.id, "submit")} loading={busy} data-testid="po-submit"><Send size={16} /> {t("Submit")}</Button>}
          {active.status === "SUBMITTED" && canApprove && <Button size="sm" variant="success" onClick={() => action(active.id, "approve")} loading={busy} data-testid="po-approve"><CheckCircle2 size={16} /> {t("Approve")}</Button>}
          {active.status === "APPROVED" && canReceive && <Button size="sm" onClick={openReceive} data-testid="po-receive"><PackageCheck size={16} /> {t("Receive")}</Button>}
          {["DRAFT", "SUBMITTED", "APPROVED"].includes(active.status) && canApprove && <Button size="sm" variant="danger" onClick={() => action(active.id, "cancel")} data-testid="po-cancel"><XCircle size={16} /> {t("Cancel")}</Button>}
        </PageHeader>

        <Card className="mb-4 grid grid-cols-2 gap-4 p-5 sm:grid-cols-4" data-testid="po-summary">
          <div><p className="text-xs text-ink-mute">{t("Status")}</p><Badge tone={PO_STATUS[active.status]}>{t(active.status)}</Badge></div>
          <div><p className="text-xs text-ink-mute">{t("Supplier")}</p><p className="font-semibold">{active.supplier_name}</p></div>
          <div><p className="text-xs text-ink-mute">{t("Source")}</p><p className="font-semibold capitalize">{t(active.source)}</p></div>
          <div><p className="text-xs text-ink-mute">{t("Total")}</p><p className="font-heading text-lg font-bold text-primary">{fmtRp(active.total_cost)}</p></div>
        </Card>

        <Card className="overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
              <th className="px-5 py-3">{t("Product")}</th><th className="px-5 py-3 text-right">{t("Qty")}</th>
              <th className="px-5 py-3 text-right">{t("Unit Price")}</th><th className="px-5 py-3 text-right">{t("Line Total")}</th></tr></thead>
            <tbody>
              {active.items.map((it, idx) => (
                <tr key={it.product_id || `custom-${idx}`} className="border-b border-line/60" data-testid={`po-item-${it.product_name}`}>
                  <td className="px-5 py-3 font-semibold">
                    {it.product_id ? (
                      <button onClick={() => navigate(`/traceability?product=${it.product_id}`)} title="Telusuri rantai pasok"
                        className="inline-flex items-center gap-1 text-left hover:text-primary" data-testid={`po-trace-${it.product_id}`}>
                        {it.product_name} <Radar size={12} className="text-ink-mute" />
                      </button>
                    ) : it.product_name}
                    {it.is_custom && <Badge tone="warn" className="ml-2">{t("Custom Item")}</Badge>}
                    {it.is_custom && it.notes && <span className="block text-xs font-normal text-ink-mute">{it.notes}</span>}
                  </td>
                  <td className="px-5 py-3 text-right">{fmtNum(it.quantity)} {fmtUnit(it.unit)}</td>
                  <td className="px-5 py-3 text-right">{fmtRp(it.unit_price)}</td>
                  <td className="px-5 py-3 text-right font-semibold">{fmtRp(it.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <div className="mt-4">
          <JournalPreview module="purchase_order" docId={active.id} title="Pratinjau Jurnal Akuntansi (Receiving)" />
        </div>

        <Modal open={receiveModal} onClose={() => setReceiveModal(false)} title={`${t("Receive")} ${active.po_no}`} size="lg"
          footer={<><Button variant="outline" onClick={() => setReceiveModal(false)}>{t("Cancel")}</Button><Button onClick={submitReceive} loading={busy} data-testid="po-receive-confirm">{t("Confirm Receipt")}</Button></>}>
          <p className="mb-3 text-xs text-ink-soft">{t("Confirm received quantities. Optionally record batch number & expiry date (recommended for perishables).")}</p>
          {receiveItems.length === 0 && <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-700" data-testid="receive-custom-only">{t("This order only has custom items (expense-only). Confirm to mark as received — no stock will be added.")}</p>}
          {receiveItems.length > 0 && active.items.some((i) => i.is_custom) && (
            <p className="mb-3 rounded-xl bg-amber-50 p-2.5 text-xs text-amber-700" data-testid="receive-mixed-hint">{t("Custom items in this order are expense-only and will not add stock.")}</p>
          )}
          <div className="space-y-3">
            {receiveItems.map((it, idx) => (
              <div key={it.product_id} className="rounded-xl border border-line p-3">
                <p className="mb-2 font-semibold">{it.product_name}</p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <div><label className="cf-label">{t("Received")}</label><input type="number" value={it.received_quantity} onChange={(e) => setReceiveItems((a) => a.map((x, i) => i === idx ? { ...x, received_quantity: e.target.value } : x))} className="cf-input" data-testid={`receive-qty-${it.product_name}`} /></div>
                  <div><label className="cf-label">{t("Unit Price")}</label><input type="number" value={it.unit_price} onChange={(e) => setReceiveItems((a) => a.map((x, i) => i === idx ? { ...x, unit_price: e.target.value } : x))} className="cf-input" /></div>
                  <div><label className="cf-label">{t("Batch No.")}</label><input value={it.batch_number} onChange={(e) => setReceiveItems((a) => a.map((x, i) => i === idx ? { ...x, batch_number: e.target.value } : x))} className="cf-input" data-testid={`receive-batch-${it.product_name}`} /></div>
                  <div><label className="cf-label">{t("Expiry")}</label><input type="date" value={it.expiry_date} onChange={(e) => setReceiveItems((a) => a.map((x, i) => i === idx ? { ...x, expiry_date: e.target.value } : x))} className="cf-input" data-testid={`receive-expiry-${it.product_name}`} /></div>
                </div>
              </div>
            ))}
          </div>
        </Modal>
      </div>
    );
  }

  // ---- List + Analytics ----
  return (
    <div className="animate-fade-up">
      <PageHeader title={t("Central Procurement")} subtitle={t("Purchase orders & cost analytics")} icon={ShoppingCart}>
        {canCreate && tab === "orders" && <Button onClick={() => { setForm({ supplier_id: "", notes: "", items: [{ product_id: "", quantity: "", unit_price: "" }] }); setCreateModal(true); }} data-testid="po-create-btn"><Plus size={18} /> {t("New Purchase Order")}</Button>}
      </PageHeader>

      <div className="mb-4 inline-flex rounded-xl border border-line bg-white p-1">
        <button onClick={() => setTab("orders")} data-testid="tab-orders" className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === "orders" ? "bg-primary text-white" : "text-ink-soft hover:bg-stone-50"}`}><Receipt size={16} /> {t("Purchase Orders")}</button>
        <button onClick={() => setTab("analytics")} data-testid="tab-analytics" className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === "analytics" ? "bg-primary text-white" : "text-ink-soft hover:bg-stone-50"}`}><BarChart3 size={16} /> {t("Cost Analytics")}</button>
      </div>

      {tab === "orders" && (
        pos.length === 0 ? <Card className="p-10 text-center text-ink-mute">{t("No purchase orders yet.")}</Card> : (
          <Card className="overflow-hidden p-0">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
                <th className="px-5 py-3">{t("PO No.")}</th><th className="px-5 py-3">{t("Supplier")}</th><th className="px-5 py-3">{t("Source")}</th>
                <th className="px-5 py-3 text-right">{t("Items")}</th><th className="px-5 py-3 text-right">{t("Total")}</th>
                <th className="px-5 py-3 text-center">{t("Status")}</th><th className="px-5 py-3">{t("Created")}</th></tr></thead>
              <tbody>
                {pos.map((po) => (
                  <tr key={po.id} className="cursor-pointer border-b border-line/60 hover:bg-stone-50/60" onClick={() => openDetail(po.id)} data-testid={`po-row-${po.po_no}`}>
                    <td className="px-5 py-3 font-mono font-semibold text-primary">{po.po_no}</td>
                    <td className="px-5 py-3">{po.supplier_name}</td>
                    <td className="px-5 py-3 capitalize text-ink-soft">{t(po.source)}</td>
                    <td className="px-5 py-3 text-right">{po.items?.length || 0}</td>
                    <td className="px-5 py-3 text-right font-semibold">{fmtRp(po.total_cost)}</td>
                    <td className="px-5 py-3 text-center"><Badge tone={PO_STATUS[po.status]}>{t(po.status)}</Badge></td>
                    <td className="px-5 py-3 text-xs text-ink-mute">{(po.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )
      )}

      {tab === "analytics" && analytics && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Card className="p-5" data-testid="analytics-total"><div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-primary-light text-primary"><TrendingUp size={20} /></div><p className="text-sm text-ink-soft">{t("Total Spend (received)")}</p><p className="font-heading text-2xl font-bold text-ink">{fmtRp(analytics.total_spend)}</p></Card>
            <Card className="p-5"><div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-success-light text-success-text"><Receipt size={20} /></div><p className="text-sm text-ink-soft">{t("Received POs")}</p><p className="font-heading text-2xl font-bold text-ink">{analytics.po_count}</p></Card>
            <Card className="p-5"><div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-700"><Building2 size={20} /></div><p className="text-sm text-ink-soft">{t("Suppliers")}</p><p className="font-heading text-2xl font-bold text-ink">{analytics.supplier_spend.length}</p></Card>
          </div>

          <Card className="p-5" data-testid="analytics-trend">
            <h3 className="mb-4 font-heading text-lg font-bold">{t("Monthly Spend Trend")}</h3>
            {analytics.monthly_spend.length === 0 ? <p className="py-6 text-center text-sm text-ink-mute">{t("No received purchase data yet.")}</p> : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={analytics.monthly_spend}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                  <XAxis dataKey="month" fontSize={12} /><YAxis fontSize={12} tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`} />
                  <Tooltip formatter={(v) => fmtRp(v)} /><Bar dataKey="spend" fill="#e8590c" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card className="overflow-hidden p-0" data-testid="analytics-top-products">
              <h3 className="border-b border-line px-5 py-4 font-heading text-lg font-bold">{t("Top Spend Products")}</h3>
              {analytics.top_spend_products.length === 0 ? <p className="px-5 py-6 text-center text-sm text-ink-mute">{t("No data")}</p> : (
                <table className="w-full text-sm"><tbody>
                  {analytics.top_spend_products.map((p) => (
                    <tr key={p.product_id} className="border-b border-line/60">
                      <td className="px-5 py-3 font-semibold">{p.product_name}</td>
                      <td className="px-5 py-3 text-right text-ink-soft">{fmtNum(p.qty)} {fmtUnit(p.unit)}</td>
                      <td className="px-5 py-3 text-right font-semibold text-primary">{fmtRp(p.spend)}</td>
                    </tr>
                  ))}
                </tbody></table>
              )}
            </Card>
            <Card className="overflow-hidden p-0" data-testid="analytics-supplier-spend">
              <h3 className="border-b border-line px-5 py-4 font-heading text-lg font-bold">{t("Supplier Spend")}</h3>
              {analytics.supplier_spend.length === 0 ? <p className="px-5 py-6 text-center text-sm text-ink-mute">{t("No data")}</p> : (
                <table className="w-full text-sm"><tbody>
                  {analytics.supplier_spend.map((s) => (
                    <tr key={s.supplier_id || "none"} className="border-b border-line/60">
                      <td className="px-5 py-3 font-semibold">{s.supplier_name}</td>
                      <td className="px-5 py-3 text-right font-semibold text-primary">{fmtRp(s.spend)}</td>
                    </tr>
                  ))}
                </tbody></table>
              )}
            </Card>
          </div>
        </div>
      )}

      {/* Create PO modal */}
      <Modal open={createModal} onClose={() => setCreateModal(false)} title={t("New Purchase Order")} size="lg"
        footer={<><Button variant="outline" onClick={() => setCreateModal(false)}>{t("Cancel")}</Button><Button onClick={createPO} data-testid="po-create-confirm">{t("Create Draft")}</Button></>}>
        <Select label={t("Supplier")} value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })} className="mb-3" data-testid="po-supplier">
          <option value="">{t("Unassigned")}</option>
          {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </Select>
        <label className="cf-label">{t("Items")}</label>
        <div className="space-y-2">
          {form.items.map((it, i) => (
            it.is_custom ? (
              <div key={i} className="rounded-xl border border-amber-200 bg-amber-50/50 p-3" data-testid={`po-custom-line-${i}`}>
                <div className="mb-2 flex items-center justify-between">
                  <Badge tone="warn">{t("Custom Item")}</Badge>
                  <button onClick={() => removeLine(i)} className="rounded-lg p-1.5 text-danger hover:bg-danger-light"><Trash2 size={15} /></button>
                </div>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-12"><input placeholder={t("Item Name")} value={it.custom_name} onChange={(e) => setLine(i, { custom_name: e.target.value })} className="cf-input" data-testid={`po-custom-name-${i}`} /></div>
                  <div className="col-span-4"><input type="number" placeholder={t("Qty")} value={it.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} className="cf-input" data-testid={`po-custom-qty-${i}`} /></div>
                  <div className="col-span-4"><input placeholder={t("Unit")} value={it.unit} onChange={(e) => setLine(i, { unit: e.target.value })} className="cf-input" data-testid={`po-custom-unit-${i}`} /></div>
                  <div className="col-span-4"><input type="number" placeholder={t("Price")} value={it.unit_price} onChange={(e) => setLine(i, { unit_price: e.target.value })} className="cf-input" data-testid={`po-custom-price-${i}`} /></div>
                  <div className="col-span-6">
                    <select value={it.supplier_id} onChange={(e) => setLine(i, { supplier_id: e.target.value })} className="cf-input" data-testid={`po-custom-supplier-${i}`}>
                      <option value="">{t("Supplier")}</option>
                      {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div className="col-span-6"><input placeholder={t("Notes")} value={it.notes} onChange={(e) => setLine(i, { notes: e.target.value })} className="cf-input" data-testid={`po-custom-notes-${i}`} /></div>
                </div>
              </div>
            ) : (
              <div key={i} className="grid grid-cols-12 items-end gap-2">
                <div className="col-span-6">
                  <select value={it.product_id} onChange={(e) => onPickProduct(i, e.target.value)} className="cf-input" data-testid={`po-line-product-${i}`}>
                    <option value="">{t("Select product...")}</option>
                    {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.unit})</option>)}
                  </select>
                </div>
                <div className="col-span-2"><input type="number" placeholder={t("Qty")} value={it.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} className="cf-input" data-testid={`po-line-qty-${i}`} /></div>
                <div className="col-span-3"><input type="number" placeholder={t("Price")} value={it.unit_price} onChange={(e) => setLine(i, { unit_price: e.target.value })} className="cf-input" /></div>
                <div className="col-span-1"><button onClick={() => removeLine(i)} className="rounded-lg p-2 text-danger hover:bg-danger-light"><Trash2 size={16} /></button></div>
              </div>
            )
          ))}
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={addLine} data-testid="po-add-line"><Plus size={14} /> {t("Add Item")}</Button>
          <Button variant="outline" size="sm" onClick={addCustomLine} data-testid="po-add-custom-line"><Plus size={14} /> {t("Add Custom Item")}</Button>
        </div>
      </Modal>
    </div>
  );
}
